// src/pages/LoginHistoryPage.jsx
import React, { useState, useRef, useEffect } from "react";
import MasterLayout from "../otherImages/MasterLayout";
import AttendanceDataTable from "../components/AttendanceDataTable";
import DefaultTopBar from "../components/DefaultTopBar";
import { useDispatch, useSelector } from "react-redux";
import {
  downloadAttendanceCSV,
  addTaskAdmin,
  clearAttendanceState,
} from "../redux/slices/attendanceSlice";
import { getAssignedAssessments } from "../redux/slices/formSlice";
import Toast from "../components/Toast";
import DynamicModal from "../components/DynamicModal";
import CreateTasksFromAssessmentsModal from "../components/CreateTasksFromAssessmentsModal";

const LoginHistoryPage = () => {
  const dispatch = useDispatch();
  const [showCreateTasksModal, setShowCreateTasksModal] = useState(false);
  const [isCreatingTasks, setIsCreatingTasks] = useState(false);

  // Refs for table refresh
  const attendanceTableRef = useRef();

  // Get download states from Redux
  const { isDownloadingCSV, downloadCSVError, successMessage } = useSelector(
    (state) => state.attendance,
  );

  // Local toast state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });
  const toastTimeoutRef = useRef(null);

  const showToast = (message, type = "info") => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({
      show: true,
      message,
      type,
    });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
      toastTimeoutRef.current = null;
    }, 3000);
  };

  const hideToast = () => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast({ show: false, message: "", type: "info" });
  };

  // Show Redux errors as toasts
  useEffect(() => {
    if (downloadCSVError) {
      showToast(downloadCSVError, "error");
      dispatch(clearAttendanceState()); // Clear the error after showing
    }
  }, [downloadCSVError, dispatch]);

  useEffect(() => {
    if (successMessage) {
      showToast(successMessage, "success");
      dispatch(clearAttendanceState()); // Clear the success message after showing
    }
  }, [successMessage, dispatch]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const handleDownloadCSV = () => {
    // Get the local time difference in hours (offset from UTC)
    const localTimeDifference = -new Date().getTimezoneOffset() / 60;

    dispatch(
      downloadAttendanceCSV({
        local_time_difference: localTimeDifference,
      }),
    );
  };

  const handleCreateTasks = () => {
    setShowCreateTasksModal(true);
  };

  const handleCloseCreateTasksModal = () => {
    setShowCreateTasksModal(false);
  };

  const handleSaveTasks = async (result) => {
    if (result.success) {
      showToast(result.message || "Task created successfully!", "success");

      // Refresh the attendance table to show new tasks
      if (
        attendanceTableRef.current &&
        attendanceTableRef.current.refreshData
      ) {
        attendanceTableRef.current.refreshData();
      }
    } else {
      showToast("Failed to create task. Please try again.", "error");
    }
  };

  return (
    <>
      {/* Toast Component */}
      {toast.show && (
        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
          duration={3000}
        />
      )}

      <MasterLayout>
        <DefaultTopBar
          title="Attendance"
          btnText="Add Attendance"
          btnLink="#"
          isApiButton={true}
          onBtnClick={handleCreateTasks}
          isBtnLoading={isCreatingTasks}
          btnLoadingText="Creating..."
          btnText2="Download CSV"
          btnLink2="#"
          isApiButton2={true}
          onBtn2Click={handleDownloadCSV}
          isBtn2Loading={isDownloadingCSV}
          btn2LoadingText="Downloading..."
        />

        <AttendanceDataTable ref={attendanceTableRef} />

        {/* Create Tasks Modal - Made wider and optimized */}
        <DynamicModal
          show={showCreateTasksModal}
          handleClose={handleCloseCreateTasksModal}
          title="Create Task from Assessment"
          modalWidth="700px" // Increased from 600px to 700px
          modalHeight="auto" // Auto height to fit content
          content={
            <div
              style={{
                maxHeight: "70vh",
                overflowY: "auto",
                padding: "0 4px",
              }}>
              <CreateTasksFromAssessmentsModal
                onClose={handleCloseCreateTasksModal}
                onSave={handleSaveTasks}
                isSaving={isCreatingTasks}
              />
            </div>
          }
        />
      </MasterLayout>
    </>
  );
};

export default LoginHistoryPage;
